import React from 'react';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell
} from 'recharts';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';

// Custom hook to detect mobile
function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 600);
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
}

const ChartContainer = styled.div`
  width: 100%;
  min-width: 0;
  min-height: 250px;
  height: 300px;
  margin: 10px 0;
  padding: 10px;
  background: ${props => props.theme.cardBg};
  border-radius: 8px;
  border: 1px solid ${props => props.theme.border};
  transition: all 0.3s ease;
`;

/** Compact container for empty / no-data state to avoid large blank area */
const ChartContainerCompact = styled(ChartContainer)`
  min-height: unset;
  height: auto;
  margin: 0.25rem 0;
  padding: 0.5rem 0.75rem;
`;

const ChartTitle = styled.h4`
  margin: 0 0 10px 0;
  color: ${props => props.theme.primaryText};
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  transition: color 0.3s ease;
`;

/** Wraps the desktop radar chart so it is centered under the Model Insights heading */
const RadarChartWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 250px;
`;

const NoDataMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${props => props.theme.secondaryText};
  font-style: italic;
  transition: color 0.3s ease;
`;

const COLORS = ['#007bff', '#28a745', '#dc3545', '#ffc107', '#17a2b8'];

/** Max features shown in chart (after excluding batting order). Matches compact default. */
const DISPLAY_MAX_FEATURES = 6;

/** Decimal places for SHAP impact / value display and axis ticks (thousandths). */
const SHAP_DECIMAL_PLACES = 3;
const SHAP_ROUND_FACTOR = 10 ** SHAP_DECIMAL_PLACES;

/**
 * Round a number for SHAP storage and chart scales.
 *
 * @param {number} n
 * @returns {number}
 */
function roundShap(n) {
  return Math.round(Number(n) * SHAP_ROUND_FACTOR) / SHAP_ROUND_FACTOR;
}

/**
 * Format a SHAP-related number for display (fixed decimal places).
 *
 * @param {number} n
 * @returns {string}
 */
function formatShapNumber(n) {
  return roundShap(n).toFixed(SHAP_DECIMAL_PLACES);
}

/**
 * Exclude lineup position from SHAP charts (aligned with backend POTD / potd_shap_drivers).
 *
 * @param {string} feature - Display label from API (e.g. "Batting Order")
 * @returns {boolean}
 */
function isShapBattingOrderFeature(feature) {
  if (feature == null || typeof feature !== 'string') return false;
  const normalized = feature.trim().toLowerCase().replace(/\s+/g, '_');
  return normalized === 'batting_order';
}

/** Dark theme for compact/embedded chart so it matches dark social card and model pages */
const COMPACT_DARK = {
  cardBg: '#2d3748',
  primaryText: '#e2e8f0',
  secondaryText: '#a0aec0',
  border: '#4a5568',
  chartGrid: '#4a5568',
  success: '#48bb78',
  error: '#fc8181',
  shadow: '0 1px 3px rgba(0,0,0,0.3)',
};

/** List layout: one row per feature with a fixed-width mini bar (fits narrow cards, no overflow) */
const ShapList = styled.div`
  width: 100%;
`;
const ShapListRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  min-height: 18px;
  &:last-child {
    margin-bottom: 0;
  }
`;
const ShapListLabel = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  font-size: 10px;
  color: ${(p) => p.$color};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: ${(p) => p.$labelMaxWidth ?? 160}px;
`;
const ShapListBarTrack = styled.div`
  flex: 0 0 72px;
  height: 10px;
  background: ${(p) => p.$track};
  border-radius: 2px;
  position: relative;
  overflow: hidden;
`;
const ShapListBarFill = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  width: ${(p) => p.$width}px;
  margin-left: ${(p) => p.$offset}px;
  background: ${(p) => p.$fill};
  border-radius: 2px;
`;
const ShapListValue = styled.span`
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 600;
  color: ${(p) => p.$color};
  width: 42px;
  text-align: right;
`;

/**
 * @param {Object} props
 * @param {Object} [props.shapData] - { feature_names, values, base_value }
 * @param {string} [props.playerName] - For chart title
 * @param {boolean} [props.forceBarLayout] - When true, always use horizontal bar chart (e.g. for embedding in social card)
 * @param {boolean} [props.compact] - When true, use dark styling for embedding in dark card
 * @param {boolean} [props.compactLayoutList] - When true with compact, use list view instead of bar chart (fits narrow cards, no overflow)
 * @param {number} [props.compactMaxFeatures] - When compact, max features to show after exclusions (capped at 6, default 6).
 * @param {number} [props.labelMaxLength] - Max characters for feature labels in list layout (default 18 for narrow cards). Use 28+ for model pages with more space.
 * @param {number|string} [props.maxWidth] - Optional max width for the chart container (e.g. 360). When set, container is centered via margin auto.
 */
const ShapRadarChart = ({
  shapData,
  playerName,
  forceBarLayout = false,
  compact = false,
  compactLayoutList = false,
  compactMaxFeatures = 6,
  labelMaxLength = 18,
  maxWidth,
}) => {
  const { theme } = useTheme();
  const isMobile = useIsMobile();
  const useBarLayout = forceBarLayout || isMobile;
  const useListLayout = compact && (compactLayoutList || forceBarLayout);
  const effectiveTheme = compact ? COMPACT_DARK : theme;
  const topNCompact = Math.max(1, Math.min(DISPLAY_MAX_FEATURES, Number(compactMaxFeatures) || DISPLAY_MAX_FEATURES));
  const chartWidth = compact ? 260 : 320;
  const chartHeight = compact ? (topNCompact <= 3 ? 100 : 60 + topNCompact * 20) : 250;
  const compactYAxisWidth = 68;
  const labelMaxLen = Math.max(18, Math.min(40, Number(labelMaxLength) || 18));
  const labelMaxWidthPx = 160 + (labelMaxLen - 18) * 6;
  
  if (!shapData || !shapData.feature_names || shapData.feature_names.length === 0) {
    return (
      <ChartContainerCompact theme={theme}>
        <ChartTitle theme={theme} style={{ marginBottom: 4 }}>Model Insights</ChartTitle>
        <NoDataMessage theme={theme} style={{ height: 'auto', minHeight: 0 }}>No model insights available</NoDataMessage>
      </ChartContainerCompact>
    );
  }

  // Pair features with values; drop batting order so it never counts toward the display cap
  const allRows = shapData.feature_names
    .map((feature, index) => ({
      feature,
      impact: roundShap(Math.abs(shapData.values[index])),
      value: roundShap(shapData.values[index]),
    }))
    .filter((d) => !isShapBattingOrderFeature(d.feature));

  if (allRows.length === 0) {
    return (
      <ChartContainerCompact theme={theme}>
        <ChartTitle theme={theme} style={{ marginBottom: 4 }}>Model Insights</ChartTitle>
        <NoDataMessage theme={theme} style={{ height: 'auto', minHeight: 0 }}>No model insights available</NoDataMessage>
      </ChartContainerCompact>
    );
  }

  const rankedByImpact = [...allRows].sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
  const displayMax = compact ? topNCompact : DISPLAY_MAX_FEATURES;
  const topForDisplay = rankedByImpact.slice(0, displayMax);

  // Radar uses top drivers by |SHAP|; bar/list reverse order for bar layout
  const radarChartData = topForDisplay;
  const topBarData = [...topForDisplay]
    .reverse()
    .map((d) => ({
      ...d,
      featureShort:
        compact && d.feature.length > labelMaxLen
          ? d.feature.slice(0, labelMaxLen).trim() + '…'
          : d.feature,
    }));
  const yAxisDataKey = compact ? 'featureShort' : 'feature';

  // Symmetric domain so no bar extends off-screen (e.g. one large positive SHAP value).
  const barValueExtent = topBarData.length
    ? Math.max(...topBarData.map((d) => Math.abs(d.value)), 0.01)
    : 1;
  const barDomain = [-barValueExtent, barValueExtent];

  // Custom tooltip for both charts (uses effectiveTheme when compact). Show full feature name when truncated on axis.
  const CustomTooltip = ({ active, payload, label, theme: tooltipTheme }) => {
    const t = tooltipTheme || effectiveTheme;
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const displayLabel = data.feature ?? label;
      return (
        <div style={{
          backgroundColor: t.cardBg,
          border: `1px solid ${t.border}`,
          padding: compact ? '6px' : '8px',
          borderRadius: '4px',
          boxShadow: t.shadow,
          color: t.primaryText
        }}>
          <p style={{ margin: '0 0 4px 0', fontWeight: 'bold' }}>{displayLabel}</p>
          <p style={{ margin: '0', color: data.value >= 0 ? t.success : t.error }}>
            Impact: {data.value >= 0 ? '+' : ''}{formatShapNumber(data.value)}
          </p>
          <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: t.secondaryText }}>
            Base Value: {formatShapNumber(shapData.base_value)}
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom legend for radar (uses effectiveTheme when compact)
  const CustomLegend = () => (
    <div style={{ textAlign: 'center', marginTop: '8px' }}>
      <div style={{ fontSize: '11px', color: effectiveTheme.secondaryText }}>
        <span style={{ color: effectiveTheme.success }}>●</span> Positive
        <span style={{ marginLeft: '12px', color: effectiveTheme.error }}>●</span> Negative
      </div>
    </div>
  );

  const resolvedMaxWidth =
    maxWidth != null && maxWidth !== ''
      ? (typeof maxWidth === 'number' ? `${maxWidth}px` : String(maxWidth))
      : undefined;

  return (
    <ChartContainer
      theme={compact ? effectiveTheme : theme}
      style={
        compact
          ? {
              minHeight: 'auto',
              height: 'auto',
              padding: '6px 8px',
              background: effectiveTheme.cardBg,
              border: `1px solid ${effectiveTheme.border}`,
              maxWidth: resolvedMaxWidth || '100%',
              width: resolvedMaxWidth ? '100%' : undefined,
              marginLeft: resolvedMaxWidth ? 'auto' : undefined,
              marginRight: resolvedMaxWidth ? 'auto' : undefined,
              boxSizing: 'border-box',
            }
          : resolvedMaxWidth
            ? {
                maxWidth: resolvedMaxWidth,
                marginLeft: 'auto',
                marginRight: 'auto',
              }
            : undefined
      }
    >
      <ChartTitle
        theme={compact ? effectiveTheme : theme}
        style={compact ? { fontSize: 11, marginBottom: 4 } : undefined}
      >
        Model Insights for {playerName}
      </ChartTitle>
      {useListLayout ? (
        <ShapList>
          {topBarData.map((entry, index) => {
            const halfBar = 36;
            const barWidth = Math.min((Math.abs(entry.value) / barValueExtent) * halfBar, halfBar);
            const offset = entry.value >= 0 ? 0 : -barWidth;
            return (
              <ShapListRow key={`shap-${index}`}>
                <ShapListLabel $color={effectiveTheme.primaryText} $labelMaxWidth={labelMaxWidthPx} title={entry.feature}>
                  {entry.featureShort ?? entry.feature}
                </ShapListLabel>
                <ShapListBarTrack $track={effectiveTheme.chartGrid}>
                  <ShapListBarFill
                    $width={barWidth}
                    $offset={offset}
                    $fill={entry.value >= 0 ? effectiveTheme.success : effectiveTheme.error}
                  />
                </ShapListBarTrack>
                <ShapListValue
                  $color={entry.value >= 0 ? effectiveTheme.success : effectiveTheme.error}
                >
                  {entry.value >= 0 ? '+' : ''}{formatShapNumber(entry.value)}
                </ShapListValue>
              </ShapListRow>
            );
          })}
          <div style={{ fontSize: 10, color: effectiveTheme.secondaryText, marginTop: 6 }}>
            <span style={{ color: effectiveTheme.success }}>●</span> Positive
            <span style={{ marginLeft: 10, color: effectiveTheme.error }}>●</span> Negative
          </div>
        </ShapList>
      ) : useBarLayout ? (
        <div style={{ width: '100%', maxWidth: compact ? '100%' : chartWidth, height: chartHeight }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={topBarData}
              layout="vertical"
              margin={{ top: 2, right: 6, left: 2, bottom: 2 }}
            >
              <CartesianGrid strokeDasharray="2 2" stroke={effectiveTheme.chartGrid} />
              <XAxis
                type="number"
                domain={barDomain}
                tick={{ fontSize: compact ? 8 : 12, fill: effectiveTheme.primaryText }}
                axisLine={{ stroke: effectiveTheme.border }}
                tickLine={{ stroke: effectiveTheme.border }}
                tickFormatter={(v) => formatShapNumber(Number(v))}
              />
              <YAxis
                dataKey={yAxisDataKey}
                type="category"
                tick={{ fontSize: compact ? 8 : 12, fill: effectiveTheme.primaryText }}
                width={compact ? compactYAxisWidth : 120}
                axisLine={{ stroke: effectiveTheme.border }}
                tickLine={false}
                interval={0}
              />
              <Tooltip content={(props) => <CustomTooltip {...props} theme={effectiveTheme} />} />
              <Bar dataKey="value" fill={effectiveTheme.primary} barSize={compact ? 12 : undefined}>
                {topBarData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.value >= 0 ? effectiveTheme.success : effectiveTheme.error}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <RadarChartWrapper>
          <RadarChart width={400} height={chartHeight} data={radarChartData}>
            <PolarGrid stroke={theme.chartGrid} />
            <PolarAngleAxis
              dataKey="feature"
              tick={{ fontSize: 10, fill: theme.primaryText }}
              tickLine={false}
            />
            <PolarRadiusAxis
              angle={90}
              domain={[0, 'dataMax + 0.1']}
              tick={{ fontSize: 10, fill: theme.secondaryText }}
              tickLine={false}
              tickFormatter={(v) => formatShapNumber(Number(v))}
            />
            <Radar
              name="Feature Impact"
              dataKey="impact"
              stroke={theme.primary}
              fill={theme.primary}
              fillOpacity={0.3}
              strokeWidth={2}
            />
            <Tooltip content={(props) => <CustomTooltip {...props} theme={theme} />} />
          </RadarChart>
        </RadarChartWrapper>
      )}
      {useBarLayout && !useListLayout && <CustomLegend />}
    </ChartContainer>
  );
};

export default ShapRadarChart; 